import type react from 'react'
import { useState, useEffect } from 'react'

import styled from '@emotion/styled'

import type { AnimationItem } from 'lottie-web'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useIsInView from '@segment/utils/useInView'
import useLottie from '@segment/utils/useLottie/useLottie'

import jsonUrl from './assets/personalization-is-no-longer-an-option-lottie.json.data'

const data = {
  title: `Personalization is no longer an option. It’s a necessity.`,
  content: [
    {
      title: `Offering personalized digital experiences used to be a nice-to-have. Now it’s essential.`,
      description: `Both businesses and consumers agree: 75% of businesses believe personalization is table stakes, while over half of consumers say personalization improves the customer experience.`,
    },
    {
      title: `What customers say:`,
      description: `“Having Segment not only helped us do the personalization work we’ve always wanted to do, but we can now improve on the effectiveness of our ad campaigns and create a better feedback loop.”`,
    },
    {
      author: `Christian Rocha Castillo`,
      position: `Deputy Director Ecommerce/Digital Media at Domino’s Pizza`,
      button: {
        title: `Read the full story`,
        href: `https://segment.com/customers/dominos/?utm_medium=referral&utm_source=segment&utm_campaign=nam_cnt_state-of-personalization_&utm_content=case-study`,
      },
    },
  ],
}

const PersonalizationIsNoLongerAnOption: react.FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])
  return (
    <SectionBase variant="2Col" reverse={true}>
      <Section.Contents>
        <Animation ref={containerRef} />
      </Section.Contents>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {data.title}
          </CopyText.Heading>
          <Content>
            {data.content.map(({ title, description, author, position, button }, key) => (
              <Item key={key}>
                {title && (
                  <CopyText.Heading tag="h3" variant="h5">
                    {title}
                  </CopyText.Heading>
                )}
                {description && <CopyText.Description variant="pMedium">{description}</CopyText.Description>}
                {author && (
                  <CopyText.Heading tag="h3" variant="h6">
                    {author}
                  </CopyText.Heading>
                )}
                {position && <Position variant="textTiny">{position}</Position>}
                {button && (
                  <ButtonText href={button.href} iconEnd={ChevronRightIcon}>
                    {button.title}
                  </ButtonText>
                )}
              </Item>
            ))}
          </Content>
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

const Animation = styled.div`
  max-width: 450px;
  margin: 0 auto;
  aspect-ratio: 450 / 650;
`

const Position = styled(CopyText.Description)`
  margin-top: -10px;
`

const Content = styled.div`
  margin-top: 48px;
`

const Item = styled.div``

export default PersonalizationIsNoLongerAnOption
